import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Phone, ArrowLeft } from "lucide-react";
import { API, useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { HansaLogo } from "../components/HansaLogo";
import { toast } from "sonner";

export default function PhoneLoginPage() {
  const { t } = useLang();
  const auth = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("phone"); // phone | otp
  const [phone, setPhone] = useState("+91");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const sendOtp = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/auth/otp/send`, { phone });
      toast.success(t("auth.otpSent"));
      setStep("otp");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send OTP");
    } finally {
      setSending(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setVerifying(true);
    try {
      const r = await axios.post(`${API}/auth/otp/verify`, {
        phone,
        code,
        name: name || undefined,
      });
      // Manually set auth state via localStorage + reload-flow used by AuthContext
      localStorage.setItem("cd_token", r.data.token);
      // Force refresh of AuthContext state
      window.location.assign("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid OTP");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center p-5">
      <div className="w-full max-w-md bg-white rounded-3xl p-8 border border-[#E5E0D8] shadow-sm cd-fade-up">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" data-testid="phone-login-logo">
            <HansaLogo size={44} />
          </Link>
          <LanguageSwitcher compact />
        </div>
        <h1 className="cd-heading text-3xl font-extrabold">{t("auth.welcomeBack")}</h1>
        <p className="text-[#52796F] mt-1 text-sm">{t("auth.welcomeBack.sub")}</p>

        {step === "phone" && (
          <form onSubmit={sendOtp} className="mt-6 space-y-4">
            <div>
              <label className="text-sm font-semibold text-[#1C1C1C]">{t("auth.phone")}</label>
              <div className="mt-1 flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#52796F] absolute ml-3 pointer-events-none" />
                <input
                  data-testid="phone-input"
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t("auth.phonePlaceholder")}
                  className="w-full bg-white border border-[#E5E0D8] rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:border-[#1B4332] focus:ring-1 focus:ring-[#1B4332]"
                />
              </div>
            </div>
            <button
              data-testid="send-otp-btn"
              disabled={sending}
              className="w-full bg-[#1B4332] text-white hover:bg-[#2D6A4F] py-3.5 rounded-full font-bold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
            >
              {sending && <Loader2 className="w-4 h-4 animate-spin" />}
              {sending ? t("auth.sendingOtp") : t("auth.sendOtp")}
            </button>
          </form>
        )}

        {step === "otp" && (
          <form onSubmit={verifyOtp} className="mt-6 space-y-4">
            <button
              type="button"
              onClick={() => setStep("phone")}
              data-testid="change-phone-btn"
              className="text-sm text-[#52796F] flex items-center gap-1 hover:text-[#1B4332]"
            >
              <ArrowLeft className="w-4 h-4" /> {t("auth.changePhone")} ({phone})
            </button>
            <div>
              <label className="text-sm font-semibold text-[#1C1C1C]">{t("auth.otp")}</label>
              <input
                data-testid="otp-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                placeholder={t("auth.otpPlaceholder")}
                className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-xl px-4 py-3 text-center text-2xl tracking-[0.5em] font-bold focus:outline-none focus:border-[#1B4332] focus:ring-1 focus:ring-[#1B4332]"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-[#1C1C1C]">
                {t("auth.firstTimeName")} {t("common.optional")}
              </label>
              <input
                data-testid="otp-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Rohan Patil"
                className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-xl px-4 py-3 focus:outline-none focus:border-[#1B4332] focus:ring-1 focus:ring-[#1B4332]"
              />
            </div>
            <button
              data-testid="verify-otp-btn"
              disabled={verifying || code.length !== 6}
              className="w-full bg-[#1B4332] text-white hover:bg-[#2D6A4F] py-3.5 rounded-full font-bold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
            >
              {verifying && <Loader2 className="w-4 h-4 animate-spin" />}
              {verifying ? t("auth.verifying") : t("auth.verifyOtp")}
            </button>
          </form>
        )}

        <p className="text-sm text-center text-[#2D3A35] mt-6">
          <Link data-testid="phone-to-login" to="/login/email" className="text-[#1B4332] font-bold hover:underline">
            {t("auth.useEmail")}
          </Link>
        </p>
      </div>
    </div>
  );
}
